import React, { useEffect, useState } from "react";
import { exportTable } from "../../utils/downloadExcel";
import { useSelector } from "react-redux";
import Main from "../Main";
import { aepsTxnList } from "../../api/aepsApi";
import { useWallet } from "../../context/walletContext";

import { formatAmount } from "../../utils/formatAmount";
import { autoDateFormat } from "../../utils/autoDateFormate";
import { useNotify } from "../../context/NotifyContext";
import WalletHistoryBar from "../../components/WalletHistoryBar";
import { useTxnDocActions } from "../../hooks/useTxnDocActions";
import {
  ListingFilters,
  ListingPagination,
  SortableTh,
  CopyText,
  AppBreadcrumbs,
  RowActions,
  useListingState,
  AEPS_STATUS_OPTIONS,
  AEPS_TXN_TYPE_OPTIONS,
  CompactSelect,
  StatusBadge,
} from "../../components/listing";

const AepsTxnHistory = () => {
  const { notify } = useNotify();
  const { walletAmt } = useWallet();
  const { userInfo } = useSelector((state) => state?.auth);
  const listing = useListingState({
    sortBy: "date",
    sortDir: "desc",
  });
  const [txnTypeStatus, setTxnTypeStatus] = useState("");
  const [settlementList, setSettalmentsList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalCharges, setTotalCharges] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const { viewTxn, printTxn, pdfTxn } = useTxnDocActions("aeps");

  const fetch_settalmentsList = async () => {
    const params = {
      ...listing.query,
      txnType: txnTypeStatus && txnTypeStatus !== "ALL" ? txnTypeStatus : undefined,
    };
    try {
      const result = await aepsTxnList(params);
      if (result?.success) {
        setSettalmentsList(result.data.aepsTransactionList || []);
        setTotalPages(result.data.pagination?.totalPages || 1);
        setTotalCount(result.data.pagination?.total || 0);
        setTotalCharges(result?.data?.totalCharges || 0);
        setTotalAmount(result?.data?.totalAmount || 0);
      } else {
        notify(result.message || "Server Error", "error");
      }
    } catch (error) {
      notify("Something went wrong", "error");
    }
  };

  useEffect(() => {
    fetch_settalmentsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listing.page,
    listing.limit,
    listing.startDate,
    listing.endDate,
    listing.debouncedSearch,
    listing.sortBy,
    listing.sortDir,
    listing.status,
    txnTypeStatus,
  ]);

  const getTxnTypeLabel = (type) => {
    switch (type) {
      case "BE":
        return "Balance Enquiry";
      case "CW":
        return "Cash Withdrawal";
      case "MS":
        return "Mini Statement";
      case "AP":
        return "Aadhaar Pay";
      case "MF":
        return "Money Transfer";
      default:
        return "--";
    }
  };

  const maskMobile = (mob) => (mob ? `XXXXXX${mob.slice(-4)}` : "--");

  const maskAadhaar = (last4) => (last4 ? `XXXX-XXXX-${last4}` : "--");

  const renderTxnInfo = (row) => {
    const ps = row?.psResponse;
    if (!ps) return "--";

    switch (row.txnType) {
      case "BE":
        return (
          <>
            <div>Balance: ₹ {ps.balanceamount ?? "--"}</div>
            <div>RRN: {ps.bankrrn ?? "--"}</div>
          </>
        );

      case "CW":
        return (
          <>
            <div>Amount: ₹ {ps.amount ?? "--"}</div>
            <div>RRN: {ps.bankrrn ?? "--"}</div>
          </>
        );

      case "MS":
        return <div>Mini Statement Available</div>;

      default:
        return <div>RRN: {ps.bankrrn ?? "--"}</div>;
    }
  };

  return (
    <Main showRightPanel={false}>
      <div className="mt-4">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: "MATM Module" },
            { label: "Other Options" },
            { label: "Aeps History" },
          ]}
        />

        <WalletHistoryBar to="/aeps/aeps-ledgers">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={`${userInfo?.logo}`}
              alt="profile"
              className="h-10 w-10 rounded-ds object-cover shrink-0"
            />
            <div className="flex flex-col md:flex-row md:items-center md:gap-2 whitespace-nowrap min-w-0">
              <p className="text-[15px] font-semibold text-[var(--text-dark)]">
                {userInfo?.userName || "GUEST"}
              </p>
              <p className="hidden md:block text-[18px] font-semibold text-[var(--text-muted)]">|</p>
              <p className="text-[12px] md:text-[15px] font-semibold text-[var(--text-muted)]">
                ID: {userInfo?.userName}
              </p>
            </div>
          </div>
          <div className="text-right font-semibold text-[10px] md:text-[15px] leading-tight w-full md:w-auto text-[var(--text-dark)]">
            AEPS WALLET BALANCE :
            <span className="make-text-primary font-bold ml-1 text-[14px] md:text-[16px]">
              ₹ {formatAmount(walletAmt?.aeps?.balance)}
            </span>
          </div>
        </WalletHistoryBar>

        <div className="ds-card text-sm">
          <ListingFilters
            onClear={listing.clearFilters}
            startDate={listing.startDate}
            endDate={listing.endDate}
            onStartDate={listing.setStartDate}
            onEndDate={listing.setEndDate}
            search={listing.search}
            onSearch={listing.setSearch}
            searchPlaceholder="Search"
            status={listing.status}
            onStatus={listing.setStatus}
            statusOptions={AEPS_STATUS_OPTIONS}
            actions={
              <>
                <label className="ds-listing-field">
                  <span className="ds-listing-field__label">Txn Type</span>
                  <CompactSelect
                    className="ds-filter-status-select"
                    value={txnTypeStatus}
                    onChange={setTxnTypeStatus}
                    options={AEPS_TXN_TYPE_OPTIONS}
                    placeholder="All Types"
                    ariaLabel="Txn Type"
                  />
                </label>
              </>
            }
            onExport={() =>
              exportTable(
                "aeps-history",
                [
                  { label: "Txn No", key: "txnId" },
                  { label: "Amount", key: "amount" },
                  { label: "Charges", key: "charges" },
                  { label: "Txn Type", value: (r) => getTxnTypeLabel(r?.txnType) },
                  { label: "Aadhaar", value: (r) => maskAadhaar(r?.aadhaarLast4) },
                  { label: "Mobile", value: (r) => maskMobile(r?.mobile) },
                  { label: "ACK No", value: (r) => r?.psResponse?.ackno ?? "" },
                  { label: "Pipe", key: "pipe" },
                  { label: "RRN", value: (r) => r?.psResponse?.bankrrn ?? "" },
                  { label: "Status", key: "status" },
                  { label: "Date", value: (r) => (r?.createdAt ? autoDateFormat(r.createdAt) : "") },
                ],
                settlementList,
                notify
              )
            }
          />

          <div className="ds-table-wrap ds-scroll">
            <table className="ds-table ds-table-listing min-w-[900px]">
              <thead>
                <tr>
                  <th>Txn No</th>
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>Charges</th>
                  <th>Txn Type</th>
                  <th>Aadhaar</th>
                  <th>Mobile</th>
                  <th>ACK No</th>
                  <th>Pipe</th>
                  <th>Info</th>
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th className="ds-th-actions">Actions</th>
                  <SortableTh
                    label="Date"
                    sortKey="date"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {settlementList?.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center text-[var(--text-muted)]">
                      NO DATA AVAILABLE IN TABLE
                    </td>
                  </tr>
                ) : (
                  settlementList?.map((row, index) => (
                    <tr key={row?._id || index}>
                      <td>
                        <CopyText value={row?.txnId} label="Txn ID" />
                      </td>
                      <td className="ds-td-amount">₹ {formatAmount(row?.amount) ?? "0"}</td>
                      <td>₹ {formatAmount(row?.charges) ?? "0"}</td>
                      <td className="font-semibold">{getTxnTypeLabel(row?.txnType)}</td>
                      <td>{maskAadhaar(row?.aadhaarLast4)}</td>
                      <td>{maskMobile(row?.mobile)}</td>
                      <td>{row?.psResponse?.ackno ?? "--"}</td>
                      <td className="uppercase">{row?.pipe ?? "--"}</td>
                      <td>
                        <div className="flex flex-col gap-1 text-[11px] text-left">
                          {renderTxnInfo(row)}
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={row?.status} />
                      </td>
                      <td>
                        <RowActions
                          onView={() => viewTxn(row)}
                          onPdf={() => pdfTxn(row)}
                          onPrint={() => printTxn(row)}
                        />
                      </td>
                      <td>{row?.createdAt ? autoDateFormat(row.createdAt) : "--"}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="ds-table-total">
                  <td className="ds-table-total__label">TOTAL TRANSFER</td>
                  <td className="ds-table-total__value">₹ {formatAmount(totalAmount)}</td>
                  <td className="ds-td-amount">₹ {formatAmount(totalCharges)}</td>
                  <td colSpan="9"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={totalCount || settlementList.length}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
          />
        </div>
      </div>
    </Main>
  );
};

export default AepsTxnHistory;
